import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DeleteIcon from '@mui/icons-material/Delete';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';

import { ButtonCreate } from 'components/ButtonCreate';
import ConnectorsListField from 'components/ConnectorsListField';
import { Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import {
  AutocompleteTextField,
  Button,
  IconButton,
  Select,
  Switch
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';
import { Tabs } from 'components/Navigation';

const formatConfig = data => {
  return data.map(el => {
    if (el.type === 'JSON') {
      return {
        id: el.id,
        name: el.name,
        connexionId: el.connexionId,
        type: 'JSON',
        config: JSON.stringify(el.config, null, 4) || '{}',
        open: el.open,
        disabled: el.disabled
      };
    }
    return el;
  });
};

const contributionOptions = [
  { label: 'Contribution consolidée', value: 'consolidated' },
  { label: 'Somme des contributions', value: 'childContributions' }
];

export const ModalForm = props => {
  const { config, funnel, onValidate, actionName } = props;
  const {
    funnels = [],
    importModel = [],
    allFunnels = [],
    configGenerators = [],
    configConnexion = [],
    mediaRegieOptions,
    getDuplicatesConfig
  } = config;

  const { themeColors } = useTheme();
  const { domainConfig } = useApp();
  const [funnelId, setFunnelId] = useState(funnel && funnel?.id);
  const isDefaultFunnel =
    !!domainConfig?.defaultFunnelId &&
    !!funnelId &&
    funnelId === domainConfig?.defaultFunnelId;
  const [menu, setMenu] = useState(
    funnel?.aggregationConfig?.length || isDefaultFunnel ? 1 : 0
  );
  const [clicked, setClicked] = useState(false);
  const [selectedContributionModel, setSelectedContributionModel] = useState(
    funnel?.useChildContributions
      ? contributionOptions[1].value
      : contributionOptions[0].value
  );

  const initialState = {
    aggregations: funnel?.aggregationConfig || [],
    emailAddress: {
      state: !!funnel.emailAddress,
      value: funnel.emailAddress?.address || ''
    },
    dailyCompute: funnel?.dailyCompute || false
  };

  const [{ aggregations, emailAddress, dailyCompute }, setState] = useState(
    initialState
  );
  const [connectorConfig, setConnectorConfig] = useState(
    funnel && funnel?.requestConfig ? formatConfig(funnel?.requestConfig) : []
  );
  const [warning, setWarning] = useState(false);
  const [errorFunnelId, setErrorFunnelId] = useState(false);

  const [duplicates, setDuplicates] = useState(
    funnel && funnel?.duplicatesConfig ? funnel.duplicatesConfig : []
  );
  const [funnelMediaVisiteRates, setFunnelMediaVisiteRates] = useState(
    funnel?.funnelMediaVisiteRates?.map(({ media, min, max }) => ({
      media,
      min: min * 100,
      max: max * 100
    })) || []
  );
  const [mediaVisiteRatesError, setMediaVisiteRatesError] = useState('');
  const lastSelectedFunnelId = useRef(funnel && funnel?.id);

  const handleChangeFunnels = value => {
    setState(prevState => ({
      ...prevState,
      aggregations: value
    }));
  };

  const onEmailChange = () => {
    setState(prevState => ({
      ...prevState,
      emailAddress: { ...emailAddress, state: !emailAddress.state }
    }));
  };

  const ondailyCompute = () => {
    setState(prevState => ({
      ...prevState,
      dailyCompute: !dailyCompute
    }));
  };

  const selectOptions = [
    { label: 'Budget', value: 'budget' },
    { label: 'Impressions', value: 'kpi1' },
    { label: 'Visites', value: 'kpi2' },
    { label: 'Engagements', value: 'kpi3' },
    { label: 'Acquisitions', value: 'kpi4' },
    { label: 'Visites par médias', value: 'volumeVisites' },
    { label: 'Clics par médias', value: 'volumeClics' },
    { label: 'Engagements par médias', value: 'volumeEngagements' },
    { label: 'Conversions par médias', value: 'volumeConversions' },
    { label: 'Envoyé par média', value: 'volumeSent' },
    { label: 'Délivré par média', value: 'volumeDelivered' },
    { label: 'Ouvert par média', value: 'volumeOpened' },
    { label: 'Appels reçus par média', value: 'receivedCalls' },
    { label: 'Appels décrochés par média', value: 'answeredCalls' },
    { label: 'Appels manqués par média', value: 'missedCalls' },
    { label: 'Durée des appels par média', value: 'totalCallsDuration' }
  ];

  const getValue = value => {
    const selected = selectOptions.find(el => el.value === value);
    return selected && selected.label;
  };

  const addDuplicata = () => {
    setDuplicates(duplicates.concat({ src: null, target: null }));
  };

  const removeDuplicata = index => {
    setDuplicates(duplicates.filter((_, fIndex) => fIndex !== index));
  };

  const handleDuplicataChange = (data, index, field) => {
    const tmp = duplicates.map((e, mapIndex) => {
      if (mapIndex === index)
        return {
          ...e,
          [field]: data
        };
      return e;
    });
    setDuplicates(tmp);
  };

  const handleChangeConnectors = newConfig => {
    setConnectorConfig(newConfig);
  };

  const validateMediaVisiteRates = list => {
    const medias = new Set();

    for (const mediaVisiteRate of list) {
      const { media, min, max } = mediaVisiteRate;
      if (!media || min === '' || max === '') {
        setMediaVisiteRatesError('Tous les champs sont requis.');
        return false;
      }

      if (
        !Number.isInteger(Number(min)) ||
        !Number.isInteger(Number(max)) ||
        Number(min) < 0 ||
        Number(max) > 100
      ) {
        setMediaVisiteRatesError(
          'Les taux min/max doivent être des entiers entre 0 et 100.'
        );
        return false;
      }

      if (Number(min) > Number(max)) {
        setMediaVisiteRatesError(
          'Le taux visite min doit être inférieur ou égal au taux visite max.'
        );
        return false;
      }

      if (medias.has(media)) {
        setMediaVisiteRatesError(
          'Un média ne peut être sélectionné qu’une fois.'
        );
        return false;
      }
      medias.add(media);
    }

    setMediaVisiteRatesError('');
    return true;
  };

  const handleCheckJson = value => {
    try {
      JSON.parse(value);
      return true;
    } catch (error) {
      return false;
    }
  };

  const onNext = async () => {
    let error = false;
    const mediaVisiteRatesAreValid = validateMediaVisiteRates(
      funnelMediaVisiteRates
    );
    if (!funnelId) {
      setErrorFunnelId(true);
      error = true;
    }
    if (menu === 0) {
      connectorConfig.map(({ type, config: configConnector }) => {
        if (type !== 'JSON' && (!configConnector || !configConnector.length)) {
          error = true;
        }
        if (type === 'JSON' && !handleCheckJson(configConnector)) {
          error = true;
        }
        return type;
      });
    }
    if (!mediaVisiteRatesAreValid) {
      error = true;
    }
    if (!error) {
      setClicked(true);
      const useChildContributions =
        selectedContributionModel === 'childContributions';
      const payload = {
        funnelId,
        duplicates: duplicates.filter(el => el.src && el.target),
        emailAddress: emailAddress.state,
        dailyCompute,
        funnelMediaVisiteRates: funnelMediaVisiteRates.map(mediaVisiteRate => ({
          ...mediaVisiteRate,
          min: Number(mediaVisiteRate.min / 100),
          max: Number(mediaVisiteRate.max / 100)
        })),
        useChildContributions
      };

      if (menu === 0 || aggregations.length === 0)
        onValidate({
          ...payload,
          requestConfig: connectorConfig.map(el => {
            return {
              ...el,
              config: el.type === 'JSON' ? JSON.parse(el.config) : el.config
            };
          })
        });
      else {
        onValidate({
          ...payload,
          aggregations: aggregations.map(
            funnelAggregated => funnelAggregated._id
          )
        });
      }
    }
  };

  const onValidation = () => {
    if (!emailAddress.state && funnel.emailAddress) {
      setWarning(true);
      return;
    }
    onNext();
  };

  const getActions = () => {
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            <Button
              variant="contained"
              color={themeColors.funnel}
              size="medium"
              widthSize="medium"
              onClick={onValidation}
              disabled={
                (menu === 0 && connectorConfig.length === 0) ||
                (menu === 1 && aggregations.length === 0)
              }
              loading={clicked}
            >
              {actionName}
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const searchOptionsKeys = ['label', 'value'];

  const onAutoCompliteChange = async data => {
    setErrorFunnelId(!data?.value);
    setFunnelId(data?.value);
    lastSelectedFunnelId.current = data?.value;

    const nextDuplicates = data?.value
      ? await getDuplicatesConfig(data.value)
      : [];

    if (lastSelectedFunnelId.current === data?.value) {
      setDuplicates(nextDuplicates);
    }
  };

  return (
    <Modal actions={getActions()} {...props}>
      {warning && (
        <Modal
          open={warning}
          maxWidth="sm"
          onClose={() => setWarning(false)}
          actions={
            <Container>
              <Row spacing={1}>
                <Item xs={6}>
                  <Button
                    variant="outlined"
                    color={themeColors.funnel}
                    onClick={() => setWarning(false)}
                  >
                    Annuler
                  </Button>
                </Item>
                <Item xs={6}>
                  <Button
                    variant="contained"
                    color={themeColors.funnel}
                    onClick={onNext}
                  >
                    Continuer et supprimer l&lsquo;email
                  </Button>
                </Item>
              </Row>
            </Container>
          }
        >
          Vous êtes sur le point de supprimer définitivement l&lsquo;adresse
          email associée à ce funnel. Tous les emails de ce compte seront
          perdus. Etes vous sur de votre choix?
        </Modal>
      )}
      <Container>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Funnel">
              <Container>
                <Row spacing={0}>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Text>Funnel *</Text>
                          <AutocompleteTextField
                            options={funnels}
                            formatLabel={option => {
                              let str = option[searchOptionsKeys[0]];
                              if (searchOptionsKeys[1]) {
                                str += ` (${option[searchOptionsKeys[1]]})`;
                              }
                              return str;
                            }}
                            small
                            value={funnels.find(el => el.value === funnelId)}
                            onChange={onAutoCompliteChange}
                            placeholder="Funnel leads"
                            disabled={funnel && funnel.id}
                            error={errorFunnelId}
                            bgColor={themeColors.primary}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} />
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row>
          <FormSection title="Duplication des données">
            {duplicates.map((e, index) => (
              <Row spacing={0} justify="space-between">
                <Item xs={4} justify="flex-start">
                  <Text>Dupliquer les données</Text>
                  <Select
                    onChange={data =>
                      handleDuplicataChange(
                        selectOptions.find(f => f.label === data).value,
                        index,
                        'src'
                      )
                    }
                    options={selectOptions.map(el => el.label)}
                    defaultValue="Aucun"
                    value={getValue(duplicates[index].src)}
                    small
                    bgColor={themeColors.primary}
                  />
                </Item>
                <Item xs />
                <Item xs={4} justify="flex-start">
                  <Text>Dans</Text>
                  <Select
                    onChange={data =>
                      handleDuplicataChange(
                        selectOptions.find(f => f.label === data).value,
                        index,
                        'target'
                      )
                    }
                    options={selectOptions.map(
                      selectOption => selectOption.label
                    )}
                    defaultValue="Aucun"
                    value={
                      duplicates[index] && getValue(duplicates[index].target)
                    }
                    small
                    bgColor={themeColors.primary}
                  />
                </Item>
                <Item xs justify="center">
                  <IconButton onClick={() => removeDuplicata(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Item>
              </Row>
            ))}
            <Row spacing={0} justify="flex-start">
              <Item xs={4} justify="flex-start">
                <ButtonCreate onClick={addDuplicata}>
                  Ajouter une donnée à dupliquer
                </ButtonCreate>
              </Item>
            </Row>
          </FormSection>
        </Row>
        <Row>
          <FormSection title="Adresse mail">
            <Row spacing={1}>
              <Item
                flex
                style={{ alignItems: 'center', justifyContent: 'flex-start' }}
              >
                <Text fontWeight={!emailAddress.state ? 600 : 300}>
                  Désactiver
                </Text>
                <Switch checked={emailAddress.state} onChange={onEmailChange} />
                <Text fontWeight={emailAddress.state ? 600 : 300}>Activer</Text>
              </Item>
              <Item xs>
                <Text fontWeight={700}>{emailAddress.value}</Text>
              </Item>
            </Row>
          </FormSection>
          <FormSection title="Contribution">
            <Row spacing={0} justify="flex-start">
              <Item xs justify="flex-start">
                {menu === 0 && (
                  <Container>
                    <Row spacing={0}>
                      <Item xs justify="flex-start">
                        <Text>MAJ quotidienne de la contribution</Text>
                      </Item>

                      <Item justify="flex-start">
                        <Text fontWeight={!dailyCompute ? 600 : 300}>
                          Désactiver
                        </Text>
                        <Switch
                          checked={dailyCompute}
                          onChange={ondailyCompute}
                        />
                        <Text fontWeight={dailyCompute ? 600 : 300}>
                          Activer
                        </Text>
                      </Item>
                    </Row>
                    <Row spacing={0}>
                      <Item justify="flex-start">
                        <Text>
                          Personnalisation taux de visites pour le modèle de
                          contribution
                        </Text>
                      </Item>
                    </Row>
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <ListField
                          values={funnelMediaVisiteRates}
                          onChange={values => {
                            setFunnelMediaVisiteRates(values);
                            validateMediaVisiteRates(values);
                          }}
                          actionTxt="Ajouter un média"
                          deleteAction
                          variant="customFields"
                          inputs={['Select', 'TextField', 'TextField']}
                          isRequireds={[true, true, true]}
                          labels={[
                            'Média',
                            'Taux visite min',
                            'Taux visite max'
                          ]}
                          options={['media', 'min', 'max']}
                          units={[null, '%', '%']}
                          fieldsFormat={[
                            mediaRegieOptions,
                            'numberDec',
                            'numberDec'
                          ]}
                          valueUnique
                        />
                      </Item>
                    </Row>
                    {!!mediaVisiteRatesError && (
                      <Row spacing={0} justify="flex-start">
                        <Item justify="flex-start">
                          <Text style={{ color: themeColors.error }}>
                            {mediaVisiteRatesError}
                          </Text>
                        </Item>
                      </Row>
                    )}
                  </Container>
                )}
                {menu === 1 && (
                  <Container>
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <Text>Modèle de calcul de contribution</Text>
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <Select
                          onChange={data => {
                            const foundOption = contributionOptions.find(
                              f => f.value === data
                            );
                            setSelectedContributionModel(foundOption?.value);
                          }}
                          options={contributionOptions}
                          value={selectedContributionModel}
                          small
                          bgColor={themeColors.primary}
                        />
                      </Item>
                    </Row>
                    <Row>
                      <Item justify="flex-start">
                        <Text>
                          {selectedContributionModel === 'consolidated'
                            ? 'La contribution sur le funnel consolidé sera calculée sur les données consolidées des funnels enfants.'
                            : 'La contribution sera calculée selon la somme des contributions des funnels enfants.'}
                        </Text>
                      </Item>
                    </Row>
                  </Container>
                )}
              </Item>
            </Row>
          </FormSection>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Données">
              <Item flex>
                <Tabs
                  startValue={menu}
                  tabs={[
                    {
                      label: 'Connecteurs',
                      onClick: () => setMenu(0),
                      disabled: isDefaultFunnel
                    },
                    {
                      label: 'Consolidation',
                      onClick: () => setMenu(1)
                    }
                  ]}
                />
              </Item>
              {menu === 0 ? (
                <ConnectorsListField
                  importModel={importModel}
                  configGenerators={configGenerators}
                  configConnexion={configConnexion}
                  value={connectorConfig}
                  onChange={handleChangeConnectors}
                />
              ) : (
                <Item>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={allFunnels
                          .filter(el => el._id !== funnelId)
                          .map(e => ({
                            name: e.name,
                            _id: e._id
                          }))}
                        searchOptionsKeys={['name', '_id']}
                        values={aggregations.map(e => ({
                          name: e.name,
                          _id: e._id
                        }))}
                        onChange={handleChangeFunnels}
                        actionTxt="Ajouter un funnel"
                        deleteAction
                        bgColor={themeColors.primary}
                      />
                    </Item>
                  </Row>
                </Item>
              )}
            </FormSection>
          </Item>
        </Row>
      </Container>
    </Modal>
  );
};

ModalForm.defaultProps = {
  funnel: {},
  actionName: null
};
ModalForm.propTypes = {
  onValidate: PropTypes.func.isRequired,
  config: PropTypes.objectOf(PropTypes.any).isRequired,
  funnel: PropTypes.objectOf(PropTypes.any),
  actionName: PropTypes.string
};

export default ModalForm;
